import React, { useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';

import Loading from './Loading';

export type CbbDropdownOptions = {
  label: string;
  value: string;
};

export type AdaptiveDropdownProps = {
  options: Array<string | CbbDropdownOptions>;
  textBeforeSelect?: string;
  nameSelected: string;
  rootClassName?: string;
  selectClassName?: string;
  iconClasses?: string;
  onChange?: (res: string) => void;
  initialSelectedOption?: string | null;
  externalStore?: boolean;
  showPlus?: boolean;
  isLoading?: boolean;
  ariaLabel?: string;
};

type AdaptiveDropdownConfig = {
  // Must mirror the desktop dropdown's own trigger classes, so callers'
  // rootClassName overrides land on the same base on both breakpoints.
  rootBaseClassName: string;
  labelClassName?: string;
  arrowIcon: any;
  arrowSize: number;
};

const getOptionValue = (option: string | CbbDropdownOptions) =>
  typeof option === 'string' ? option : option.value;

const getOptionLabel = (option: string | CbbDropdownOptions) =>
  typeof option === 'string' ? option : option.label;

const getInitialValue = (
  options: Array<string | CbbDropdownOptions>,
  initialSelectedOption?: string | null
) => {
  const match = options.find(
    (option) =>
      getOptionValue(option) === initialSelectedOption ||
      getOptionLabel(option) === initialSelectedOption
  );
  return match ? getOptionValue(match) : '';
};

// Native <select> on mobile (best-practice UX), desktop dropdown unchanged.
// Shared by CbbDropdown and CbbDropdownV2 via the injected desktop component.
export const createAdaptiveDropdown = (
  DesktopDropdown: React.ComponentType<AdaptiveDropdownProps>,
  config: AdaptiveDropdownConfig
): React.FC<AdaptiveDropdownProps> => {
  const { rootBaseClassName, labelClassName, arrowIcon, arrowSize } = config;

  const AdaptiveDropdown: React.FC<AdaptiveDropdownProps> = (props) => {
    const {
      options,
      textBeforeSelect,
      nameSelected,
      rootClassName,
      onChange,
      initialSelectedOption,
      externalStore,
      showPlus,
      isLoading,
      iconClasses,
      ariaLabel,
    } = props;

    const isMobile = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('md')
    );

    const resolvedValue = getInitialValue(
      options,
      initialSelectedOption ?? nameSelected
    );
    const [selectedValue, setSelectedValue] = useState(resolvedValue);
    // focus/blur is the only signal a native <select> gives for its picker.
    const [isOpen, setIsOpen] = useState(false);

    // Keep in sync when the parent's value changes externally (e.g. clear filters, URL restore).
    useEffect(() => {
      if (!externalStore) {
        setSelectedValue(resolvedValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedValue, externalStore]);

    if (!isMobile) {
      return <DesktopDropdown {...props} />;
    }

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      // iOS defers blur until the picker finishes dismissing; change fires as
      // soon as the option is picked, so close on it to drop that lag.
      setIsOpen(false);
      if (!externalStore) {
        setSelectedValue(value);
      }
      onChange && onChange(value);
    };

    // externalStore means the parent owns the selected value (nameSelected),
    // same as the desktop dropdown's own getSelectedValue() behavior.
    const value = externalStore
      ? getInitialValue(options, nameSelected)
      : selectedValue;

    const selectedLabel = (() => {
      if (externalStore || !value) {
        return nameSelected;
      }
      const match = options.find((option) => getOptionValue(option) === value);
      return match ? getOptionLabel(match) : nameSelected;
    })();

    const displayedValue = isLoading ? <Loading inline /> : selectedLabel;

    // The visible box is the desktop trigger, pixel for pixel; the native
    // <select> is transparent on top of it so iOS draws no chevron of its own.
    const selectElement = (
      <div className={`${rootBaseClassName} ${rootClassName || ''}`}>
        {/* truncate keeps a long value on one line: the box is a fixed
            h-[44px] at most call sites, so wrapping spills past the border. */}
        <span
          className={`pointer-events-none min-w-0 truncate ${
            labelClassName || ''
          }`}
        >
          {textBeforeSelect} {displayedValue}
          {showPlus && displayedValue !== 'Any' && '+'}
        </span>
        <div
          className={`w-6 h-6 contents flex-shrink-0 pointer-events-none ${
            iconClasses || ''
          }`}
        >
          <Image
            className={`transition duration-300 !pt-[2px] ${
              isOpen ? 'transform rotate-90' : ''
            }`}
            src={arrowIcon}
            alt=""
            aria-hidden="true"
            height={arrowSize}
            width={arrowSize}
          />
        </div>
        <select
          value={value}
          onChange={handleChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          disabled={isLoading}
          aria-label={ariaLabel || textBeforeSelect || nameSelected}
          className="absolute inset-0 !w-full !h-full !m-0 !p-0 !border-0 opacity-0 appearance-none bg-transparent cursor-pointer"
        >
          <option value="" disabled>
            {nameSelected}
          </option>
          {options.map((option) => {
            const optionValue = getOptionValue(option);
            const label = getOptionLabel(option);
            return (
              <option key={optionValue} value={optionValue}>
                {label}
                {showPlus && option !== 'Any' && '+'}
              </option>
            );
          })}
        </select>
      </div>
    );

    return selectElement;
  };

  return AdaptiveDropdown;
};
